import * as THREE from 'three';

// LOOK VDJ - what separates a club VJ loop from a generic visualizer, as a
// small shared toolbox. A visualizer that "applies the VDJ look" uses these
// helpers instead of re-deriving them:
//
//  1. Deep black and saturated neon: vdjColor() keeps the cover's HUE and
//     imposes saturation >= 0.9 and lightness 0.55. Whatever glows also gets
//     a white core above the bloom threshold (0.78) - that part lives in the
//     visualizer's own shader.
//  2. Cuts on the bar or the phrase, never fades: a policy, not code - change
//     state only on a bar/phrase edge, and change it in one frame.
//  3. 4 / 8 / 16-beat loops: every cyclic motion's period is exactly that
//     many beats (see vdjLoop), so the image lands on its feet with the music.
//  4. RGB split on the beat: vdjBeatChroma() is a multiplier for the filmic
//     pass's EXISTING chromatic aberration (x4 for 80 ms, then exponential
//     decay); VdjPost turns it into the `postProcessing` object scene-manager
//     reads every frame. No new pass.
//  5. Symmetries: VDJ_MIRROR_CHUNK, applied to the UVs in the visualizer's
//     own final shader, changed on the phrases.
//  6. Nervous camera: vdjCameraKick() - on each bar a zoom +4% or a roll
//     +-3 deg that comes back in one beat, ease-out.
//  7. Drop flash: one frame of white at 0.85 (never 1.0), through
//     VdjComfort so it never breaks the comfort rules.
//
// VISUAL COMFORT, above everything else: never more than 3 full-screen
// flashes a second, mean luminance under 0.45 over one second. VdjComfort
// enforces both - a flash budget and a luminance governor fed by whatever
// measurement the visualizer can afford.

export const VDJ_SATURATION = 0.9;
export const VDJ_LIGHTNESS = 0.55;
export const VDJ_DROP_FLASH = 0.85;
export const VDJ_FLASHES_PER_S = 3;
export const VDJ_MEAN_LUM_MAX = 0.45;
const CHROMA_HOLD_S = 0.08;
const CHROMA_TAU_S = 0.07;
const CHROMA_PEAK = 4;
const KICK_ZOOM = 0.04;
const KICK_ROLL = 3 * Math.PI / 180;
const BASE_ABERRATION = 0.0022;    // scene-manager's POST_DEFAULTS.aberration

const _hsl = { h: 0, s: 0, l: 0 };
const _c = new THREE.Color();

// paletteHex (number, '#rrggbb' or THREE.Color) -> a THREE.Color in LINEAR
// space: the cover's hue, saturation >= sat, lightness imposed. `out` is
// optional. `sat` lets a visualizer push to 1.0 for its most saturated look
// (after a drop).
export function vdjColor(paletteHex, out, sat) {
  const o = out || new THREE.Color();
  _c.set(paletteHex).getHSL(_hsl);
  o.setHSL(_hsl.h, Math.max(sat != null ? sat : VDJ_SATURATION, _hsl.s), VDJ_LIGHTNESS);
  return o;
}

// The aberration multiplier for the time elapsed in the current beat:
// CHROMA_PEAK for the first 80 ms, then back to 1 exponentially.
// beatPhase in [0, 1), beatSeconds the beat's length.
export function vdjBeatChroma(beatPhase, beatSeconds) {
  const t = Math.max(0, beatPhase) * (beatSeconds || 0.5);
  if (t < CHROMA_HOLD_S) return CHROMA_PEAK;
  return 1 + (CHROMA_PEAK - 1) * Math.exp(-(t - CHROMA_HOLD_S) / CHROMA_TAU_S);
}

// A phase in [0, 1) that loops over exactly `periodBeats` beats (4, 8 or 16),
// from a continuous beat count.
export function vdjLoop(beats, periodBeats) {
  const p = beats / periodBeats;
  return p - Math.floor(p);
}

// The bar's camera kick. barPhase in [0, 1); barIndex picks the gesture
// (zoom on even bars, roll alternating sign on odd ones). The kick lands on
// the downbeat and eases out over the first beat (a quarter of the bar).
export function vdjCameraKick(barPhase, barIndex, beatsPerBar) {
  const beatFrac = 1 / (beatsPerBar || 4);
  const u = Math.min(1, Math.max(0, barPhase) / beatFrac);
  const k = (1 - u) * (1 - u) * (1 - u);          // ease-out back to rest
  const i = barIndex || 0;
  if (i % 2 === 0) return { zoom: 1 + KICK_ZOOM * k, roll: 0 };
  const sign = (i >> 1) % 2 === 0 ? 1 : -1;
  return { zoom: 1, roll: sign * KICK_ROLL * k };
}

// Mirror modes, applied to a UV in [0, 1]^2 by the visualizer's final shader.
export const VDJ_MIRROR_NONE = 0, VDJ_MIRROR_H = 1, VDJ_MIRROR_QUAD = 2, VDJ_MIRROR_KALEIDO6 = 3, VDJ_MIRROR_KALEIDO8 = 4;
export const VDJ_MIRROR_NAMES = { none: 0, h: 1, quad: 2, kaleido: 3, kaleido8: 4 };

// GLSL: vec2 vdjMirror(vec2 uv, float mode, float aspect). The kaleidoscope
// folds the angle about the centre into one 60-degree wedge, mirrored, in
// aspect-correct space so the wedges are true 60 degrees.
export const VDJ_MIRROR_CHUNK = `
  vec2 vdjMirror(vec2 uv, float mode, float aspect) {
    if (mode < 0.5) return uv;
    if (mode < 1.5) return vec2(0.5 - abs(uv.x - 0.5), uv.y);
    if (mode < 2.5) return vec2(0.5 - abs(uv.x - 0.5), 0.5 - abs(uv.y - 0.5));
    vec2 p = (uv - 0.5) * vec2(aspect, 1.0);
    float r = length(p);
    float a = atan(p.y, p.x);
    float w = mode < 3.5 ? 1.0471975512 : 0.7853981634;   // 60 degrees (six wedges), or 45 (mode 4: eight)
    a = mod(a, 2.0 * w);
    a = abs(a - w);
    p = vec2(cos(a), sin(a)) * r;
    // The frame's corners reach past the source (r ~ 1 at 16:9): mirror the
    // UV back in rather than clamp (a clamp smears the edge into streaks)
    // or zoom (which magnifies whatever sits at the centre).
    vec2 q = p / vec2(aspect, 1.0) + 0.5;
    return 1.0 - abs(1.0 - mod(q, 2.0));
  }
`;

// The `postProcessing` object for scene-manager, rebuilt only when the
// aberration actually changes (scene-manager compares by identity every
// frame; a new object costs one _applyPostProfile, which only writes
// uniforms when the tone mapping is unchanged).
export class VdjPost {
  constructor(base) {
    this._base = Object.assign({}, base || {});
    this._value = null;
    this._last = -1;
  }
  update(chromaFactor) {
    const ab = Math.round(BASE_ABERRATION * chromaFactor * 1e5) / 1e5;
    if (ab !== this._last) {
      this._last = ab;
      this._value = Object.assign({}, this._base, { aberration: ab });
    }
    return this._value;
  }
  get value() { return this._value || this.update(1); }
}

// The comfort rules. requestFlash(nowS) says whether a full-screen flash may
// fire now (at most VDJ_FLASHES_PER_S in any second); measure(meanLum, dtS)
// feeds a one-second average of the frame's mean luminance and returns the
// gain the visualizer must apply so that average stays under the ceiling.
export class VdjComfort {
  // options (all optional, the party defaults otherwise):
  //   meanLumMax   the one-second luminance ceiling (lounge: 0.35)
  //   flashesPerS  full-screen flashes allowed per second (lounge: 0 - none)
  constructor(options) {
    const o = options || {};
    this._meanMax = o.meanLumMax != null ? o.meanLumMax : VDJ_MEAN_LUM_MAX;
    this._flashMax = o.flashesPerS != null ? o.flashesPerS : VDJ_FLASHES_PER_S;
    this._flashes = [];
    this._avg = 0;
    this._gain = 1;
  }
  requestFlash(nowS) {
    while (this._flashes.length && nowS - this._flashes[0] > 1) this._flashes.shift();
    if (this._flashes.length >= this._flashMax) return false;
    this._flashes.push(nowS);
    return true;
  }
  // meanLum is measured AFTER the current gain; dtS the time it covers.
  measure(meanLum, dtS) {
    const k = Math.min(1, dtS / 1.0);
    this._avg += (meanLum - this._avg) * k;
    const target = 0.9 * this._meanMax;
    if (this._avg > target) this._gain *= Math.max(0.7, target / this._avg);
    else this._gain = Math.min(1, this._gain * 1.02);
    return this._gain;
  }
  get gain() { return this._gain; }
  get average() { return this._avg; }
}

// ---------------------------------------------------------------------------
// PRESETS. 'party' is everything above. 'lounge' keeps the deep black, the
// saturated neon (0.85), the white cores, the shiny 3D and the symmetries,
// and changes the rhythm:
//   - cuts on the PHRASE, never on the bar, and only one phrase in two;
//     between two cuts everything is continuous, and a one-bar FADE is
//     allowed as a transition (forbidden in party);
//   - long loops: 16, 32 or 64 beats;
//   - the beat is a BREATH, not a hit: +8% luminance at most, 80 ms attack,
//     400 ms decay (vdjBeatBreath) - if you can tap your foot to it, it is
//     too strong;
//   - RGB split on the BAR only, x 1.5, 300 ms decay (vdjBarChroma);
//   - camera: slow drift, no kick (vdjCameraKick is not used);
//   - drop: no flash; +25% intensity over one bar, then the visualizer's
//     richest variant for 8 bars;
//   - comfort: no full-screen flash at all, mean luminance under 0.35 over
//     one second (new VdjComfort(VDJ_PRESETS.lounge.comfort)).
export const VDJ_PRESETS = {
  party: {
    saturation: VDJ_SATURATION, cutEvery: 'bar', fades: false, loops: [4, 8, 16],
    breath: null, chroma: 'beat', kick: true, dropFlash: VDJ_DROP_FLASH,
    comfort: { meanLumMax: VDJ_MEAN_LUM_MAX, flashesPerS: VDJ_FLASHES_PER_S }
  },
  lounge: {
    saturation: 0.85, cutEvery: 'phrase2', fades: true, fadeBars: 1, loops: [16, 32, 64],
    breath: { gain: 0.08, attackS: 0.08, decayS: 0.40 }, chroma: 'bar', kick: false,
    dropFlash: 0, dropRise: { gain: 0.25, bars: 1 }, dropRichBars: 8,
    comfort: { meanLumMax: 0.35, flashesPerS: 0 }
  }
};

// The lounge beat: a luminance MULTIPLIER, 1 at rest, 1 + gain at the top of
// an 80 ms attack, back over 400 ms (exponential, tau = decay / 3).
// sinceBeatS: seconds since the last beat edge.
export function vdjBeatBreath(sinceBeatS, preset) {
  const b = (preset || VDJ_PRESETS.lounge).breath;
  if (!b) return 1;
  const t = Math.max(0, sinceBeatS);
  if (t < b.attackS) {
    const u = t / b.attackS;
    return 1 + b.gain * u * u * (3 - 2 * u);
  }
  return 1 + b.gain * Math.exp(-(t - b.attackS) / (b.decayS / 3));
}

// The lounge RGB split: an aberration multiplier peaking at 1.5 on the
// downbeat and decaying over 300 ms (tau 100 ms). sinceBarS: seconds since
// the last bar edge. Feed it to VdjPost.update().
export function vdjBarChroma(sinceBarS) {
  return 1 + 0.5 * Math.exp(-Math.max(0, sinceBarS) / 0.1);
}
